// src/_services/firebase.ts
import React from 'react'
import { initializeApp, type FirebaseOptions } from 'firebase/app'
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  onAuthStateChanged,
  type NextOrObserver,
  type User,
} from 'firebase/auth'
import {
  getDatabase,
  ref,
  get,
  set,
  update,
  remove,
  push,
  onValue,
  off,
  type DataSnapshot,
} from 'firebase/database'
import { fb_config } from '../_config/fb_config'
import type { AdminRecord, LogAction } from '../_types'
import { DB_ADMIN_BY_ID, DB_LOG_ENTRY } from '../_config/db_paths'

// ─── decode config ───────────────────────────────────────────
function dstring(s: string) {
  return s
}

const firebaseConfig: FirebaseOptions = {
  apiKey: dstring(fb_config.a),
  authDomain: dstring(fb_config.b),
  databaseURL: dstring(fb_config.c),
  projectId: dstring(fb_config.d),
  storageBucket: dstring(fb_config.e),
  messagingSenderId: dstring(fb_config.f),
  appId: dstring(fb_config.g),
}

const app = initializeApp(firebaseConfig)
export const auth = getAuth(app)
export const db = getDatabase(app)

// ─── Auth Context ────────────────────────────────────────────
export const AuthContext = React.createContext<{
  currentUser: User | null
  setCurrentUser: (u: User | null) => void
  signOut: () => void
}>({
  currentUser: null,
  setCurrentUser: () => { },
  signOut: () => { },
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = React.useState<User | null>(null)

  React.useEffect(() => {
    return onAuthStateChanged(auth, (u) => setCurrentUser(u ?? null))
  }, [])

  const signOut = () => {
    fbSignOut(auth)
    setCurrentUser(null)
    window.location.reload()
  }

  return (
    <AuthContext.Provider value={{ currentUser, setCurrentUser, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

// ─── Auth helpers ────────────────────────────────────────────
export const firebaseSignIn = (email: string, pw: string) =>
  signInWithEmailAndPassword(auth, email, pw)

export const firebaseSignOut = () => fbSignOut(auth)

export const firebaseUserStats = (cb: NextOrObserver<User | null>) =>
  onAuthStateChanged(auth, cb)

// ─── RTDB helpers ────────────────────────────────────────────
export async function dbRead<T = any>(path: string): Promise<T | null> {
  const snap = await get(ref(db, path))
  return snap.exists() ? (snap.val() as T) : null
}

export async function dbSet(path: string, data: any): Promise<void> {
  await set(ref(db, path), data)
}

export async function dbUpdate(path: string, data: Record<string, any>): Promise<void> {
  await update(ref(db, path), data)
}

export async function dbRemove(path: string): Promise<void> {
  await remove(ref(db, path))
}

export async function dbPush(path: string, data: any): Promise<string> {
  const r = await push(ref(db, path), data)
  return r.key ?? ''
}

/** realtime listener — คืน unsubscribe fn */
export function dbListen<T = any>(
  path: string,
  cb: (val: T | null) => void
): () => void {
  const r = ref(db, path)
  onValue(r, (snap: DataSnapshot) => cb(snap.exists() ? snap.val() : null))
  return () => off(r)
}

// ─── Admin check ─────────────────────────────────────────────
export async function getAdminRecord(uid: string): Promise<AdminRecord | null> {
  return dbRead<AdminRecord>(DB_ADMIN_BY_ID(uid))
}

// ─── Action Log ──────────────────────────────────────────────
export async function saveLog(
  uid: string,
  action: LogAction,
  target: string,
  detail?: string
): Promise<void> {
  const log: Record<string, any> = {
    uid,
    action,
    target,
    date: Date.now(),
  }
  if (detail !== undefined && detail !== null && detail !== '') {
    log.detail = detail
  }
  const logId = `${Date.now()}_${uid.slice(0, 6)}`
  await dbSet(DB_LOG_ENTRY(logId), log)
}

// ─── PIN helpers ─────────────────────────────────────────────
export async function sha256Hex(input: string): Promise<string> {
  const enc = new TextEncoder().encode(input)
  const buf = await crypto.subtle.digest('SHA-256', enc)
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

export async function getShallowKeys(path: string): Promise<string[]> {
  const dbUrl  = process.env.REACT_APP_DATABASE_URL
  if (!dbUrl) throw new Error('ไม่พบ REACT_APP_DATABASE_URL')
  
  let token = ''
  try {
    const user = auth.currentUser
    if (user) token = await user.getIdToken()
  } catch {}

  const url  = `${dbUrl.replace(/\/$/, '')}/${path}.json?shallow=true${token ? `&auth=${token}` : ''}`
  const resp = await fetch(url)
  if (!resp.ok) throw new Error(`Firebase shallow read failed: ${resp.status}`)
  const data = await resp.json()
  if (!data || typeof data !== 'object') return []
  return Object.keys(data)
}
